/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo } from 'react';
import { useLocation } from '@jira/navigation-apps__routing-hooks/src/useLocation.tsx';
import { useRoute } from '@jira/navigation-apps__routing-hooks/src/useRoute.tsx';
import { useConnectGeneralResource } from '@jira/router-resources__connect-general/src/useConnectGeneralResource.tsx';
import type { ConnectDataItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/types.tsx';
import { findItemAndStack } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/find-item-stack/index.tsx';
import { getLinks } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/get-links/index.tsx';
import { transformLink } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-connect/src/common/utils/transform-link/index.tsx';
import { useNavigationGlobalResource } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/controllers/navigation-global-resource/index.tsx';
import type { SidebarConfigData } from '@jira/navigation-apps__shared-types/src/types.tsx';
import type { RouteResourceError } from '@atlassian/react-resource-router';

const getItems = (d: SidebarConfigData) =>
	getLinks(d, ['admin_plugins_menu'], { skipFilter: true })
		.filter((v) => !['upm_section', 'jpo-ws-admin'].includes(v.id)) // remove sections also statically defined
		.map(transformLink) // Apply transformation to each link
		.filter(Boolean); // Remove falsy values

export const useGlobalSettingsConnectItems = (): {
	data: ConnectDataItem[] | null;
	loading: boolean;
	error: RouteResourceError | null;
} => {
	const { data, loading, error } = useNavigationGlobalResource();

	const items: ConnectDataItem[] | null = useMemo(() => data && getItems(data), [data]);

	return {
		data: items,
		loading,
		error,
	};
};

export const APPS_SETTINGS_CONNECT_APP_MODULE_TYPES: Set<string> = new Set<string>([
	'configurePage',
	'postInstallPage',
]);

export const useIsAppsSettingsConnectAppModule = (): {
	isActive: boolean;
	loading: boolean;
	error: // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| Record<string, any>
		| Error // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Record<string, any> & Error) // oxlint-disable-next-line @typescript-eslint/no-explicit-any
		| (Error & Record<string, any>)
		| null;
} => {
	const { data, loading, error } = useConnectGeneralResource();

	return {
		isActive: APPS_SETTINGS_CONNECT_APP_MODULE_TYPES.has(data?.moduleType),
		loading,
		error,
	};
};

export const useGlobalAppsSettingsConnectItemActive = (): {
	isActive: boolean;
	loading: boolean;
	error: RouteResourceError | null;
} => {
	const location = useLocation();
	const route = useRoute();

	const { data, loading, error } = useGlobalSettingsConnectItems();
	const {
		isActive: isAppsSettingsConnectAppModuleActive,
		loading: isAppsSettingsConnectAppModuleLoading,
		error: isAppsSettingsConnectAppModuleError,
	} = useIsAppsSettingsConnectAppModule();

	const isActive = useMemo(
		() => Boolean(route && data && findItemAndStack(data, route.name, location)?.selected),
		[data, route, location],
	);

	return {
		isActive: isActive || isAppsSettingsConnectAppModuleActive,
		loading: loading || isAppsSettingsConnectAppModuleLoading,
		error: error || isAppsSettingsConnectAppModuleError,
	};
};
